import { FC, useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { MLInput } from 'components/Input/Input';
import Toggle from 'components/Common/Toggle';
import { ClientFormValues, clientManageSchema, ClientFormValuesWithObject } from 'api/clients/types';
import useCreateClientWithObject from 'hooks/Clients/useCreateClientWithObject';
import useGetClient from 'hooks/Clients/useGetClient';
import useUpdateClient from 'hooks/Clients/useUpdateClient';
import useGetClientOwners from 'hooks/Clients/useGetClientOwners';
import { useCreateOwnerForClient } from 'hooks/Clients/smartUsersForClientCreate/useCreateOwnerForClient';
import { useCreateTechnicianForClient } from 'hooks/Clients/smartUsersForClientCreate/useCreateTechnicianForClient';
import useGetAvailableTechnicians from 'hooks/Object/useGetAvailableTechnicians';

interface ManageClientModalProps {
  clientId?: number;
  isOpen: boolean;
  onClose: () => void;
  refetchClients: () => void;
}

const ManageClientModal: FC<ManageClientModalProps> = ({ clientId, isOpen, onClose, refetchClients }) => {
  const [isObjectCreating, setIsObjectCreating] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<'clientCreation' | 'technicianCreation' | 'ownerCreation'>(
    'clientCreation',
  );
  const { data } = useGetClient(clientId);

  const { createOwner, isLoading: isLoadingOwner } = useCreateOwnerForClient();
  const { createTechnician, isLoading: isLoadingTechnician } = useCreateTechnicianForClient();

  const { clientOwners, refetch: refetchClientOwners } = useGetClientOwners();
  const { data: availableTechnicians, refetch: refetchAvailableTechnicians } = useGetAvailableTechnicians();

  const { createNewClientWithObject } = useCreateClientWithObject();
  const { updateClient } = useUpdateClient();
  const { t } = useTranslation();

  // Form for client creation/update
  const { handleSubmit, control, reset, setError, watch } = useForm<ClientFormValues>({
    resolver: zodResolver(clientManageSchema),
    defaultValues: {
      name: '',
      EDRPOU: '',
      business_structure: '',
      country: '',
      city: '',
      zip_code: '',
      street: '',
      building_no: '',
      apartment_no: '',
      owner: '',
      image: '',
    },
  });

  // Form for owner/technician creation
  const {
    handleSubmit: handleOwnerSubmit,
    control: ownerControl,
    reset: resetOwner,
    setError: setOwnerError,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: '',
    },
  });
  // for the technician creation form
  const {
    handleSubmit: handleTechnicianSubmit,
    control: technicianControl,
    reset: resetTechnician,
    setError: setTechnicianError,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: '',
      is_lead: false,
    },
  });

  const ownerOptions =
    !clientOwners || clientOwners.length === 0
      ? [{ value: '', label: t('Modals.manage-object.noOwnersToSet') }]
      : [
          { value: '', label: t('Modals.manage-object.select-owner') },
          ...clientOwners.map(owner => ({
            value: owner.id.toString(),
            label: owner.last_name ? `${owner.last_name} ${owner.first_name}` : owner.email,
          })),
        ];

  const technicianOptions =
    !availableTechnicians || availableTechnicians.length === 0
      ? [{ value: '', label: t('Modals.manage-client.noTechniciansAvailable') }]
      : [
          { value: '', label: t('Modals.manage-object.selectTechnician') },
          ...availableTechnicians.map(technician => ({
            value: technician.id.toString(),
            label: technician.label || technician.email,
          })),
        ];

  const onReset = () => {
    reset();
    resetOwner();
    resetTechnician();
    setCurrentTab('clientCreation');
    setIsObjectCreating(false);
  };

  const onCreateNewClient = (data: ClientFormValues) => {
    if (isObjectCreating) {
      // Create client with object
      const clientData: ClientFormValuesWithObject = {
        name: data.name,
        EDRPOU: data.EDRPOU || '',
        business_structure: data.business_structure || '',
        country: data.country || '',
        city: data.city || '',
        zip_code: data.zip_code || '',
        street: data.street || '',
        building_no: data.building_no || '',
        apartment_no: data.apartment_no || '',
        full_name: data.name, // Using name as full_name for object
        owner: typeof data.owner === 'string' && data.owner ? parseInt(data.owner) : null,
        technician: data.technician ? parseInt(data.technician) : null,
      };

      createNewClientWithObject(
        {
          client: clientData,
          isObjectCreating: true,
        },
        {
          onSuccess: () => {
            const foundOwner = data.name;
            const foundTechnician = data.name;
            const ownerName = foundOwner || foundOwner || 'Unknown Company';

            enqueueSnackbar(t('Modals.add-create-client', { ownerName }), {
              variant: 'success',
            });
            if (foundTechnician) {
              const technicianName = foundTechnician || foundTechnician || 'Unknown Company';

              enqueueSnackbar(t('Modals.add-create-site', { technicianName }), {
                variant: 'success',
              });
            }
            onReset();
            onClose();
            refetchClients();
          },
          onError: (error: any) => {
            handleCreateError(error);
          },
        },
      );
    } else {
      // Create simple client
      const clientData: ClientFormValuesWithObject = {
        name: data.name,
        EDRPOU: data.EDRPOU || '',
        business_structure: data.business_structure || '',
        country: data.country || '',
        city: data.city || '',
        zip_code: data.zip_code || '',
        street: data.street || '',
        building_no: data.building_no || '',
        apartment_no: data.apartment_no || '',
        full_name: data.name,
        owner: typeof data.owner === 'string' && data.owner ? parseInt(data.owner) : null,
        technician: null,
      };

      createNewClientWithObject(
        {
          client: clientData,
          isObjectCreating: false,
        },
        {
          onSuccess: () => {
            const foundOwner = data.name;
            const ownerName = foundOwner || foundOwner || 'Unknown Company';

            enqueueSnackbar(t('Modals.add-create-client', { ownerName }), {
              variant: 'success',
            });

            onReset();
            onClose();
            refetchClients();
          },
          onError: (error: any) => {
            handleCreateError(error);
          },
        },
      );
    }
  };

  const handleCreateError = (error: any) => {
    if (error.response?.data?.owner) {
      if (error.response.data.owner?.non_field_errors) {
        setError('owner.confirm_password', { message: error.response.data.owner.non_field_errors[0] });
        setError('owner.password', { message: error.response.data.owner.non_field_errors[0] });
      }
      if (error.response.data.owner?.email) {
        setError('owner.email', { message: error.response.data.owner.email[0] });
      }
    }
    if (error.response?.data?.name) {
      setError('name', { message: t('Modals.manage-object.nameAlreadyExists') });
    }
    if (error.response?.data?.EDRPOU) {
      setError('EDRPOU', { message: t('Modals.manage-client.edrpouError') });
    }
  };

  const handleOwnerError = (error: any) => {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.first_name) {
        setOwnerError('first_name', { message: errorData.first_name[0] });
      }
      if (errorData.last_name) {
        setOwnerError('last_name', { message: errorData.last_name[0] });
      }
      if (errorData.email) {
        setOwnerError('email', { message: errorData.email[0] });
      }
      if (errorData.phone) {
        setOwnerError('phone', { message: errorData.phone[0] });
      }
      if (errorData.password) {
        setOwnerError('password', { message: errorData.password[0] });
      }
      if (errorData.confirm_password) {
        setOwnerError('confirm_password', { message: errorData.confirm_password[0] });
      }

      // Handle non-field errors (like password mismatch)
      if (errorData.non_field_errors) {
        setOwnerError('confirm_password', { message: errorData.non_field_errors[0] });
        setOwnerError('password', { message: errorData.non_field_errors[0] });
      }
    } else {
      enqueueSnackbar(t('Modals.manage-owner.failCreate'), { variant: 'error' });
    }
  };

  const handleTechnicianError = (error: any) => {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.first_name) {
        setTechnicianError('first_name', { message: errorData.first_name[0] });
      }
      if (errorData.last_name) {
        setTechnicianError('last_name', { message: errorData.last_name[0] });
      }
      if (errorData.email) {
        setTechnicianError('email', { message: errorData.email[0] });
      }
      if (errorData.password) {
        setTechnicianError('password', { message: errorData.password[0] });
      }
      if (errorData.confirm_password) {
        setTechnicianError('confirm_password', { message: errorData.confirm_password[0] });
      }

      // Handle non-field errors (like password mismatch)
      if (errorData.non_field_errors) {
        setTechnicianError('confirm_password', { message: errorData.non_field_errors[0] });
        setTechnicianError('password', { message: errorData.non_field_errors[0] });
      }
    } else {
      enqueueSnackbar(t('Modals.manage-technician.failCreate'), { variant: 'error' });
    }
  };

  const onUpdateClient = (data: ClientFormValues) => {
    const safeClientData = {
      ...data,
      city: data.city ?? '',
      street: data.street ?? '',
      building_no: data.building_no ?? '',
    };
    updateClient(
      {
        id: clientId,
        client: safeClientData,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-edit'), { variant: 'success' });
          onReset();
          onClose();
        },
        onError: (error: any) => {
          if (error.response.data.name) {
            setError('name', { message: t('Modals.manage-object.nameAlreadyExists') });
          }
          if (error.response.data.EDRPOU) {
            setError('EDRPOU', { message: t('Modals.manage-client.edrpouError') });
          }
        },
      },
    );
  };

  const onCreateOwner = (ownerData: any) => {
    createOwner(ownerData, {
      onSuccess: response => {
        enqueueSnackbar(t('Modals.manage-owner.successCreate'), { variant: 'success' });
        refetchClientOwners();
        setCurrentTab('clientCreation');
        // Set the newly created owner as selected
        const currentValues = watch();
        reset({ ...currentValues, owner: response.id.toString() });
      },
      onError: (error: any) => {
        handleOwnerError(error);
      },
    });
  };

  const onCreateTechnician = (technicianData: any) => {
    createTechnician(technicianData, {
      onSuccess: response => {
        enqueueSnackbar(t('Modals.manage-technician.successCreate'), { variant: 'success' });
        refetchAvailableTechnicians();
        setCurrentTab('clientCreation');
        // Set the newly created technician as selected
        const currentValues = watch();
        reset({ ...currentValues, technician: response.id.toString() });
      },
      onError: (error: any) => {
        handleTechnicianError(error);
      },
    });
  };

  useEffect(() => {
    if (clientId) {
      reset({
        name: data?.name || '',
        EDRPOU: data?.EDRPOU || '',
        city: data?.city || '',
        street: data?.street || '',
        building_no: data?.building_no || '',
        apartment_no: data?.apartment_no || '',
        owner: data?.owner?.toString() || '',
        image: data?.image && typeof data.image === 'string' ? data.image : undefined,
      });
    }
  }, [data, reset, clientId]);

  const renderClientCreationTab = () => (
    <div className="">
      <div className="row">
        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <MLInput type="text" label={t('Modals.manage-client.name')} error={error?.message} required {...field} />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="EDRPOU"
            render={({ field, fieldState: { error } }) => (
              <MLInput label={t('Modals.manage-client.EDRPOU')} type="text" error={error?.message} {...field} />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="business_structure"
            render={({ field, fieldState: { error } }) => (
              <MLInput
                label={t('Modals.manage-client.business_structure')}
                type="text"
                error={error?.message}
                {...field}
              />
            )}
          />
        </div>

        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="country"
            render={({ field, fieldState: { error } }) => (
              <MLInput {...field} error={error?.message} label={t('Modals.manage-client.country')} />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="city"
            render={({ field, fieldState: { error } }) => (
              <MLInput {...field} error={error?.message} label={t('Modals.manage-client.city')} required />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="zip_code"
            render={({ field, fieldState: { error } }) => (
              <MLInput {...field} error={error?.message} label={t('Modals.manage-client.zip')} required />
            )}
          />
        </div>

        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="street"
            render={({ field, fieldState: { error } }) => (
              <MLInput {...field} error={error?.message} label={t('Modals.manage-client.street')} required />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="building_no"
            render={({ field, fieldState: { error } }) => (
              <MLInput {...field} error={error?.message} label={t('Modals.manage-client.building_no')} required />
            )}
          />
        </div>

        <div className="mb-3 col-md-4">
          <Controller
            control={control}
            name="apartment_no"
            render={({ field, fieldState: { error } }) => (
              <MLInput label={t('Modals.manage-client.apartment_no')} error={error?.message} {...field} />
            )}
          />
        </div>

        {/* Object Creation Toggle - Only show for new clients */}
        {!clientId && (
          <div className="mb-3 col-12">
            <Toggle
              checked={isObjectCreating}
              onChange={checked => setIsObjectCreating(checked)}
              label={t('Modals.manage-client.createSiteSameAsCompany')}
              size="sm"
            />
          </div>
        )}

        {/* Owner Selection */}
        <div className="col-12 col-md-6 mb-3">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <Controller
                control={control}
                name="owner"
                rules={{ required: true }}
                render={({ field, formState: { errors } }) => (
                  <div>
                    <label className="form-label mb-1">
                      {t('Modals.manage-client.owner')}
                      <span className="text-danger m-1">*</span>
                    </label>
                    <Select
                      {...field}
                      required
                      options={ownerOptions}
                      value={ownerOptions.find(option => option.value === field.value) || null}
                      onChange={selectedOption => field.onChange(selectedOption ? selectedOption.value : '')}
                      placeholder={t('Modals.manage-client.owner')}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: errors?.owner?.message ? 'red' : base.borderColor,
                          boxShadow: state.isFocused ? '0 0 0 2px rgba(95, 0, 204, 0.5)' : 'none',
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? '#7700ff'
                            : state.isFocused
                              ? '#b683e6'
                              : base.backgroundColor,
                          color: state.isSelected || state.isFocused ? 'white' : 'black',
                        }),
                        singleValue: base => ({
                          ...base,
                          color: 'black',
                        }),
                      }}
                    />
                    {errors.owner && <p className="text-danger">{t('Modals.manage-object.fieldRequired')}</p>}
                  </div>
                )}
              />
            </div>
            <button
              className="btn btn-ghost-success fs-15 ms-2"
              style={{
                width: '40px',
                height: '38px',
                marginTop: '20px',
              }}
              title={t('Modals.manage-client.createNewOwner')}
              onClick={() => setCurrentTab('ownerCreation')}
              type="button"
            >
              +
            </button>
          </div>
          <div className="form-text text-muted mt-1">{t('Modals.manage-client.helper-text')}</div>
        </div>
        {/* Technician Selection - Only show when object creation is enabled */}
        {isObjectCreating && !clientId && (
          <>
            <div className="col-12 col-md-6">
              <div className="row mb-3">
                <div className="col mb-3">
                  <Controller
                    control={control}
                    name="technician"
                    rules={{ required: isObjectCreating }}
                    render={({ field, formState: { errors } }) => (
                      <div>
                        <label className="form-label mb-1">
                          {t('Modals.manage-technician.technician')}
                          <span className="text-danger m-1">*</span>
                        </label>
                        <Select
                          {...field}
                          required={isObjectCreating}
                          options={technicianOptions}
                          value={technicianOptions.find(option => option.value === field.value) || null}
                          onChange={selectedOption => field.onChange(selectedOption ? selectedOption.value : '')}
                          placeholder={t('Modals.manage-object.selectTechnician')}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              borderColor: errors?.technician?.message ? 'red' : base.borderColor,
                              boxShadow: state.isFocused ? '0 0 0 2px rgba(95, 0, 204, 0.5)' : 'none',
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? '#7700ff'
                                : state.isFocused
                                  ? '#b683e6'
                                  : base.backgroundColor,
                              color: state.isSelected || state.isFocused ? 'white' : 'black',
                            }),
                            singleValue: base => ({
                              ...base,
                              color: 'black',
                            }),
                          }}
                        />
                        {errors.technician && <p className="text-danger">{t('Modals.manage-object.fieldRequired')}</p>}
                      </div>
                    )}
                  />
                  <div>
                    <small className="text-muted mt-2">{t('Modals.manage-technician.help-text')}</small>
                  </div>
                </div>

                <div className="col-auto mb-3 mt-4">
                  <button
                    className="btn btn-ghost-success fs-15 ms-2"
                    style={{ width: '40px' }}
                    title={t('Modals.manage-client.createNewTechnician')}
                    onClick={() => setCurrentTab('technicianCreation')}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderOwnerCreationTab = () => (
    <div className="row">
      <div className="col-12">
        <h5 className="mb-1">{t('Modals.manage-owner.title')}</h5>
        <h6 className="mb-4 text-muted">{t('Modals.manage-owner.subtitle')}</h6>
        <div className="row mb-3">
          <div className="col-md-6">
            <Controller
              control={ownerControl}
              name="first_name"
              render={({ field, fieldState: { error } }) => (
                <MLInput {...field} label={t('Modals.manage-owner.firstName')} error={error?.message} required />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={ownerControl}
              name="last_name"
              render={({ field, fieldState: { error } }) => (
                <MLInput {...field} label={t('Modals.manage-owner.lastName')} error={error?.message} required />
              )}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <Controller
              control={ownerControl}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-owner.email')}
                  type="email"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={ownerControl}
              name="phone"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-owner.phone')}
                  type="phone"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Controller
              control={ownerControl}
              name="password"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-owner.password')}
                  type="password"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={ownerControl}
              name="confirm_password"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-owner.confirmPassword')}
                  type="password"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTechnicianCreationTab = () => (
    <div className="row">
      <div className="col-12">
        <h5 className="mb-3">{t('Modals.manage-technician.title')}</h5>
        <div className="row mb-3">
          <div className="col-md-6">
            <Controller
              control={technicianControl}
              name="first_name"
              render={({ field, fieldState: { error } }) => (
                <MLInput {...field} label={t('Modals.manage-technician.firstName')} error={error?.message} required />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={technicianControl}
              name="last_name"
              render={({ field, fieldState: { error } }) => (
                <MLInput {...field} label={t('Modals.manage-technician.lastName')} error={error?.message} required />
              )}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <Controller
              control={technicianControl}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-technician.email')}
                  type="email"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={technicianControl}
              name="phone"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-technician.phone')}
                  type="phone"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Controller
              control={technicianControl}
              name="password"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-technician.password')}
                  type="password"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={technicianControl}
              name="confirm_password"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  label={t('Modals.manage-technician.confirmPassword')}
                  type="password"
                  error={error?.message}
                  required
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <Controller
            control={technicianControl}
            name="is_lead"
            render={({ field }) => (
              <div className=" d-flex gap-2 mt-2">
                <input
                  className="ml-1"
                  type="checkbox"
                  checked={field.value}
                  onChange={() => field.onChange(!field.value)}
                />
                <span className=" d-flex align-items-center ml-1"> {t('Modals.addTechnicianModal.team-leader')}</span>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (currentTab) {
      case 'clientCreation':
        return renderClientCreationTab();
      case 'ownerCreation':
        return renderOwnerCreationTab();
      case 'technicianCreation':
        return renderTechnicianCreationTab();
      default:
        return renderClientCreationTab();
    }
  };

  const renderFooterButtons = () => {
    if (currentTab === 'ownerCreation') {
      return (
        <>
          <Button className="btn btn-ghost-danger" onClick={() => setCurrentTab('clientCreation')}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button
            className="btn btn-ghost-success"
            onClick={handleOwnerSubmit(onCreateOwner)}
            disabled={isLoadingOwner}
          >
            {isLoadingOwner ? t('Modals.manage-client.creating') : t('Modals.manage-owner.createOwner')}
          </Button>
        </>
      );
    }

    if (currentTab === 'technicianCreation') {
      return (
        <>
          <Button className="btn btn-ghost-danger" onClick={() => setCurrentTab('clientCreation')}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button
            className="btn btn-ghost-success"
            onClick={handleTechnicianSubmit(onCreateTechnician)}
            disabled={isLoadingTechnician}
          >
            {isLoadingTechnician ? t('Modals.manage-client.creating') : t('Modals.manage-technician.createTechnician')}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          className="btn btn-ghost-danger"
          onClick={() => {
            onReset();
            onClose();
          }}
        >
          {t('Modals.delete-modal.cancel')}
        </Button>
        <Button
          className="btn btn-ghost-success"
          onClick={clientId ? handleSubmit(onUpdateClient) : handleSubmit(onCreateNewClient)}
        >
          {clientId ? t('Modals.manage-client.edit.title') : t('Modals.manage-client.create.title')}
        </Button>
      </>
    );
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          onReset();
          onClose();
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          className="border-bottom py-3 mx-3"
          toggle={() => {
            onReset();
            onClose();
          }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-5">
            <div className="">
              {clientId
                ? t('Modals.manage-client.edit.title')
                : currentTab === 'clientCreation'
                  ? t('Modals.manage-client.create.title')
                  : currentTab === 'ownerCreation'
                    ? t('Modals.manage-owner.header')
                    : t('Modals.manage-technician.header-client')}
            </div>
            {!clientId && currentTab !== 'clientCreation' && (
              <Button className="btn btn-ghost-purple btn-sm" onClick={() => setCurrentTab('clientCreation')}>
                {t('Modals.manage-client.backToClientCreation')}
              </Button>
            )}
          </div>
        </ModalHeader>
        <ModalBody className="mt-3">{renderTabContent()}</ModalBody>
        <ModalFooter>{renderFooterButtons()}</ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageClientModal;
